import { useMemo } from 'react';
import type { QuoteFreightLeg } from '../types/quote-freight.types';

interface IncotermFlags {
  paysInlandOrigin: boolean;
  paysInternational: boolean;
  paysInlandDest: boolean;
  paysHandling: boolean;
  paysInsurance: boolean;
  paysDuties: boolean;
}

const LEG_TYPE_TO_CONFIG_FIELD: Record<string, keyof IncotermFlags> = {
  INLAND_ORIGIN: 'paysInlandOrigin',
  INTERNATIONAL: 'paysInternational',
  INLAND_DESTINATION: 'paysInlandDest',
  HANDLING: 'paysHandling',
};

export interface UseQuoteFreightParams {
  legs: QuoteFreightLeg[];
  totalWeight: number;
  totalVolume: number;
  totalFob: number;
  incotermConfig: IncotermFlags | null;
  provisionRate?: number;
}

export type CalculatedLeg = QuoteFreightLeg & {
  baseAmount: number;
  surchargeTotal: number;
  insuranceAmount: number;
  calculatedAmount: number;
  includedByIncoterm: boolean;
  provisionAmount: number;
  calcDescription: string;
  surcharges: (QuoteFreightLeg['surcharges'][number] & { calculatedAmount: number })[];
};

export function useQuoteFreightCalculation({
  legs,
  totalWeight,
  totalVolume,
  totalFob,
  incotermConfig,
  provisionRate = 0,
}: UseQuoteFreightParams) {
  return useMemo(() => {
    const defaultConfig: IncotermFlags = {
      paysInlandOrigin: true,
      paysInternational: true,
      paysInlandDest: true,
      paysHandling: true,
      paysInsurance: true,
      paysDuties: true,
    };
    const config = incotermConfig || defaultConfig;

    let totalFreight = 0;
    let totalInsurance = 0;
    let totalBase = 0;
    let totalSurcharges = 0;
    let totalProvision = 0;

    const calculatedLegs: CalculatedLeg[] = legs.map((leg) => {
      const configField = LEG_TYPE_TO_CONFIG_FIELD[leg.legType];
      const included = configField ? config[configField] : true;

      let baseAmount = 0;
      let calcDescription = '';
      switch (leg.chargeMethod) {
        case 'CBM':
          baseAmount = totalVolume * leg.ratePerCbm;
          calcDescription = `${totalVolume} m³ × $${leg.ratePerCbm}/m³`;
          break;
        case 'KG':
          baseAmount = totalWeight * leg.ratePerKg;
          calcDescription = `${totalWeight} kg × $${leg.ratePerKg}/kg`;
          break;
        case 'FLAT':
          baseAmount = leg.flatRate;
          calcDescription = 'Monto fijo';
          break;
        case 'MANUAL':
          baseAmount = leg.manualAmount;
          calcDescription = 'Monto manual';
          break;
      }
      if (leg.minimumCharge && baseAmount < leg.minimumCharge) {
        baseAmount = leg.minimumCharge;
        calcDescription += ` (mín. $${leg.minimumCharge})`;
      }

      const surcharges = leg.surcharges.map((s) => ({
        ...s,
        calculatedAmount: s.type === 'PERCENTAGE' ? (baseAmount * s.value) / 100 : s.value,
      }));
      const surchargeTotal = surcharges.reduce((sum, s) => sum + s.calculatedAmount, 0);

      let insuranceAmount = 0;
      if (leg.hasInsurance && leg.insuranceRate > 0) {
        insuranceAmount = (totalFob * leg.insuranceRate) / 100;
      }

      const subtotal = baseAmount + surchargeTotal + insuranceAmount;
      const provisionAmount = provisionRate > 0 ? (subtotal * provisionRate) / 100 : 0;
      const calculatedAmount = subtotal + provisionAmount;

      if (included) {
        totalFreight += calculatedAmount;
        totalInsurance += insuranceAmount;
        totalBase += baseAmount;
        totalSurcharges += surchargeTotal;
        totalProvision += provisionAmount;
      }

      return {
        ...leg,
        baseAmount,
        surchargeTotal,
        insuranceAmount,
        calculatedAmount,
        includedByIncoterm: included,
        surcharges,
        provisionAmount,
        calcDescription,
      };
    });

    return {
      totalFreight,
      totalInsurance,
      totalBase,
      totalSurcharges,
      totalProvision,
      paysInsurance: config.paysInsurance,
      paysDuties: config.paysDuties,
      legs: calculatedLegs,
    };
  }, [legs, totalWeight, totalVolume, totalFob, incotermConfig, provisionRate]);
}
